import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Mail, Phone, User, MessageSquare, Send, CheckCircle, ShieldCheck } from 'lucide-react';

const Contact = () => {
  const navigate = useNavigate();
  const appName = process.env.REACT_APP_APP_NAME || 'CRM Automax';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api('/support/contact', {
        method: 'POST',
        body: formData
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess(true);
        setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setError(data.message || 'Failed to submit contact request');
      }
    } catch (err) {
      setError('Connection error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 12px 12px 40px',
    borderRadius: '8px', border: '1px solid #e2e8f0',
    fontSize: '14px', outline: 'none',
    background: '#ffffff', color: '#1e293b',
    transition: 'all 0.2s ease', boxSizing: 'border-box',
  };

  const iconStyle = { position: 'absolute', left: '12px', top: '12px', color: '#94a3b8' };

  const navLinkStyle = {
    fontSize: '14px', fontWeight: '600', color: '#475569',
    textDecoration: 'none', transition: 'color 0.2s ease', cursor: 'pointer'
  };

  const Navbar = () => (
    <nav className="marketing-navbar" style={{
      position: 'sticky', top: 0, zIndex: 1000,
      background: 'rgba(255, 255, 255, 0.8)', backdropFilter: 'blur(12px)',
      borderBottom: '1px solid #f1f5f9', padding: '0 48px', height: 'auto', minHeight: '72px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap'
    }}>
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', color: '#1e293b' }}>
        <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <ShieldCheck size={20} color="white" />
        </div>
        <span style={{ fontWeight: '800', fontSize: '18px', letterSpacing: '-0.02em' }}>{appName}</span>
      </Link>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap', padding: '10px 0' }}>
        <Link to="/#features" className="nav-link" style={navLinkStyle}>Features</Link>
        <Link to="/pricing" className="nav-link" style={navLinkStyle}>Pricing</Link>
        <Link to="/contact" className="nav-link" style={{ ...navLinkStyle, color: '#3b82f6' }}>Contact</Link>
        <div style={{ width: '1px', height: '24px', background: '#e2e8f0', margin: '0 8px' }} />
        <Link to="/login" style={{ ...navLinkStyle, color: '#3b82f6' }}>Log in</Link>
        <button onClick={() => navigate('/register')} style={{
          background: '#3b82f6', color: 'white', border: 'none',
          padding: '10px 24px', borderRadius: '10px', fontWeight: '700',
          fontSize: '14px', cursor: 'pointer', transition: 'all 0.2s ease'
        }}>
          Get Started
        </button>
      </div>
    </nav>
  );

  const Footer = () => (
    <footer className="marketing-footer" style={{ padding: '80px 48px 40px', borderTop: '1px solid #f1f5f9', background: '#ffffff' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', gap: '60px', flexWrap: 'wrap', marginBottom: '60px' }}>
        <div style={{ flex: '0 0 300px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShieldCheck size={16} color="white" />
            </div>
            <span style={{ fontWeight: '800', fontSize: '16px', color: '#1e293b' }}>{appName}</span>
          </div>
          <p style={{ color: '#64748b', fontSize: '14px', lineHeight: 1.6 }}>
            The cloud-based sales automation platform built for performance-driven teams. 
            Simplify lead management, boost conversions, and scale effortlessly.
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '80px' }}>
          <div>
            <div style={{ fontWeight: '700', marginBottom: '20px', color: '#1e293b' }}>Product</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link to="/pricing" style={{ textDecoration: 'none', color: '#64748b', fontSize: '14px' }}>Pricing</Link>
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '700', marginBottom: '20px', color: '#1e293b' }}>Legal</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Link to="/privacy-policy" style={{ textDecoration: 'none', color: '#64748b', fontSize: '14px' }}>Privacy Policy</Link>
              <Link to="/terms" style={{ textDecoration: 'none', color: '#64748b', fontSize: '14px' }}>Terms & Conditions</Link>
            </div>
          </div>
          <div>
            <div style={{ fontWeight: '700', marginBottom: '20px', color: '#1e293b' }}>Contact</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <a href="mailto:info@westernflex.com" style={{ textDecoration: 'none', color: '#64748b', fontSize: '14px' }}>info@westernflex.com</a>
              <div style={{ color: '#64748b', fontSize: '14px' }}>+91 86699 30119 (Sales)</div>
            </div>
          </div>
        </div>
      </div>
      
      <div style={{ textAlign: 'center', paddingTop: '40px', borderTop: '1px solid #f1f5f9', color: '#94a3b8', fontSize: '13px' }}>
        © {new Date().getFullYear()} {appName}. All rights reserved. Built with excellence in India.
      </div>
    </footer>
  );

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>
        <Navbar />
        <div className="contact-content-wrapper" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div style={{ textAlign: 'center', background: 'white', padding: '48px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
            <CheckCircle size={64} color="#10b981" style={{ margin: '0 auto 24px' }} />
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '16px' }}>Message Sent!</h2>
            <p style={{ color: '#64748b', marginBottom: '32px' }}>Thank you for reaching out. We will get back to you shortly.</p>
            <button onClick={() => setSuccess(false)} style={{ padding: '12px 24px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
              Send Another Message
            </button>
            <div style={{ marginTop: '24px' }}>
              <Link to="/" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: '500' }}>Return to Home</Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f8fafc', fontFamily: "'Inter', sans-serif" }}>
      <Navbar />
      <div className="contact-content-wrapper" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 24px' }}>
        <div style={{ width: '100%', maxWidth: '600px', background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '40px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
        
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '8px' }}>Contact Us</h1>
          <p style={{ color: '#64748b' }}>Have questions? We'd love to hear from you.</p>
        </div>

        {error && (
          <div style={{ background: '#fef2f2', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', marginBottom: '24px', fontSize: '14px', border: '1px solid #fee2e2' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ position: 'relative' }}>
            <User size={18} style={iconStyle} />
            <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Your Name" style={inputStyle} />
          </div>

          <div style={{ position: 'relative' }}>
            <Mail size={18} style={iconStyle} />
            <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="Email Address *" style={inputStyle} />
          </div>

          <div style={{ position: 'relative' }}>
            <Phone size={18} style={iconStyle} />
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} required placeholder="Phone Number *" style={inputStyle} />
          </div>

          <div style={{ position: 'relative' }}>
            <MessageSquare size={18} style={iconStyle} />
            <input type="text" name="subject" value={formData.subject} onChange={handleChange} placeholder="Subject" style={inputStyle} />
          </div>

          <div style={{ position: 'relative' }}>
            <textarea name="message" value={formData.message} onChange={handleChange} required placeholder="How can we help you? *" style={{ ...inputStyle, minHeight: '120px', paddingLeft: '16px', paddingTop: '16px' }} />
          </div>

          <button type="submit" disabled={loading} style={{ width: '100%', padding: '14px', background: loading ? '#93c5fd' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', transition: 'all 0.2s ease' }}>
            {loading ? 'Sending...' : <><Send size={18} /> Send Message</>}
          </button>
        </form>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <Link to="/" style={{ color: '#64748b', textDecoration: 'none', fontSize: '14px', fontWeight: '500' }}>← Back to Home</Link>
        </div>
      </div>
      </div>
      <Footer />
    </div>
  );
};

export default Contact;
